import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { Auth } from '@angular/fire/auth';
import { environment } from '../../environments/environment';
import { AuthApiService } from './auth-api.service';
import { 
  RegisterUserRequest as NewRegisterUserRequest,
  GetUserAccountRequest,
  ApiResult,
  GetUserAccountWithStatusResponse
} from '../types/auth.types';

// Legacy interface for backward compatibility
export interface RegisterUserRequest {
  mobile: string;
  email?: string;
  name?: string;
  type?: string;
  emailVerified?: boolean;
  mobileVerified?: boolean;
  uid?: string;
}

export interface ApiResponse<T> {
  success?: boolean;
  message?: string;
  data?: T;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private auth: Auth,
    private authApiService: AuthApiService
  ) {
    console.log('🔧 API service initialized with base URL:', this.baseUrl);
  }

  /**
   * Get HTTP headers with authorization token
   */
  private async getHeaders(): Promise<HttpHeaders> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    
    // Get token directly from Firebase Auth to avoid circular dependency
    if (this.auth.currentUser) {
      try {
        const token = await this.auth.currentUser.getIdToken();
        headers = headers.set('Authorization', `Bearer ${token}`);
      } catch (error) {
        console.warn('⚠️ [ApiService] Failed to get ID token:', error);
      }
    }
    
    return headers;
  }

  /**
   * Register user with the backend API (Legacy method)
   * POST /api/auth/register
   * @deprecated Use AuthApiService.registerUser() instead
   */
  async registerUser(userData: RegisterUserRequest): Promise<any> {
    console.log('🚀 [ApiService] Registering user (legacy method)...');
    
    try {
      // Convert legacy format to new format
      const newUserData: NewRegisterUserRequest = {
        mobile: userData.mobile,
        email: userData.email,
        name: userData.name,
        type: userData.type as 'individual' | 'agency' || 'individual',
        emailVerified: userData.emailVerified,
        mobileVerified: userData.mobileVerified
      };

      // Use new auth API service
      const result = await this.authApiService.registerUser(newUserData).toPromise();
      
      console.log('✅ [ApiService] Registration successful');
      return result?.data;
    } catch (error) {
      console.error('❌ [ApiService] Registration failed:', error);
      throw error;
    }
  }

  /**
   * Get user profile from backend API
   * GET /api/users/me
   */
  async getUserProfile(): Promise<any> {
    console.log('🔍 [ApiService] Fetching user profile...');
    
    try {
      const headers = await this.getHeaders();
      const url = `${this.baseUrl}/users/me/profile`;
      
      const response = await this.http.get(url, { headers }).toPromise();
      
      console.log('✅ [ApiService] User profile fetched');
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Failed to fetch user profile:', error);
      throw error;
    }
  }


  /**
   * Update user profile
   * PUT /api/users/me/profile
   */
  async updateUserProfile(profileData: { email?: string; name?: string; profile?: any }): Promise<any> {
    console.log('🔄 [ApiService] Updating user profile...');
    
    try {
      const headers = await this.getHeaders();
      const url = `${this.baseUrl}/users/me/profile`;
      
      const response = await this.http.put(url, profileData, { headers }).toPromise();
      
      console.log('✅ [ApiService] User profile updated');
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Failed to update user profile:', error);
      throw error;
    }
  }

  registerUserWithErrorHandling(userData: NewRegisterUserRequest): Observable<ApiResult<any>> {
    return this.authApiService.registerUser(userData);
  }
}
